import AIPilot from './AIPilot.js';
import { ENEMIES, ROADS } from '../core/SimConfig.js';
import { clamp, rand, srand } from '../utils.js';

// Scratch results — these are read and consumed within a single call, so one
// shared instance each keeps the per-frame driver tick allocation-free.
const _sample = { x: 0, z: 0, tx: 0, tz: 0 };
const _trailHit = { x: 0, z: 0, tx: 0, tz: 0, speed: 0, short: false };
const _trailProj = { distBack: 0, speed: 0, dist: 0 };

// Floats per breadcrumb: x, z, and the speed the vehicle was doing there.
const TRAIL_STRIDE = 3;

// How many edges of route are committed ahead of the vehicle. Only needs to
// cover the steering lookahead, but going a little deeper means a junction is
// resolved well before it's reached rather than on the frame it arrives.
const ROUTE_QUEUE_MAX = 6;

const wrapPi = (a) => {
  while (a >  Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
};

/**
 * Ground-vehicle AI driver — planar analogue of HelicopterAIPilot.
 *
 *   - explore: pick a random world waypoint, drive to it, repeat (default)
 *   - path:    follow the baked road graph, taking junctions at random
 *   - follow:  trail the assigned leader along the line it actually drove
 *   - loiter:  brake to a stop, hold heading
 *   - disabled: vehicle wreck — AI is bypassed; motion model coasts to halt
 *
 * Each tick sets the motion model's desiredHeading + desiredSpeed; the motion
 * model handles steering, throttle, and terrain conformance.
 *
 * Every driver also records a breadcrumb trail of where it has been, which is
 * what its follower steers along. See ENEMIES.vehicleFormation for why the
 * column tracks a recorded line instead of a slot in the leader's frame.
 */
export default class AIDriver extends AIPilot {
  constructor(params, initialState = 'explore', roadNetwork = null) {
    super();
    this.params = params;
    this.state = initialState;
    this.roads = roadNetwork;

    this.waypoint = null;            // { x, z }
    this._waypointReactionTimer = 0;

    // Follow state — mirrors HelicopterAIPilot fields so EnemyManager's
    // leader-promotion logic works without special casing.
    this.leader = null;
    this._followGap = 0;
    this._followLateral = 0;
    this._lastTrailBack = 0;         // search hint for projectTrail

    // Route state (path mode). `_route` is a queue of committed traversals,
    // head first; `_routeS` is arc length along the head edge in that edge's
    // own from→to parameterisation, so travel moves it by ±speed·dt per `dir`.
    this._route = [];
    this._routeS = 0;

    // Breadcrumb ring buffer — see _recordTrail.
    const f = ENEMIES.vehicleFormation;
    this._trailCap = Math.ceil(f.trailLength / f.trailSampleDist) + 2;
    this._trail = new Float32Array(this._trailCap * TRAIL_STRIDE);
    this._trailCount = 0;
    this._trailHead = 0;             // index of the most recent sample
    this._trailTipX = 0;             // live position, ahead of the newest crumb
    this._trailTipZ = 0;
    this._trailTipSpeed = 0;
  }

  _randomReactionDelay() {
    const r = ENEMIES.reactionDelay;
    return rand(r.minDelay, r.maxDelay);
  }

  /** Enter follow state targeting `leader`. Offsets randomized within config. */
  followLeader(leader) {
    const f = ENEMIES.vehicleFormation;
    this.leader = leader;
    this._followGap = f.spacing + srand() * f.spacingJitter;
    this._followLateral = srand() * f.lateralJitter;
    // Seeded at the nominal gap, which is where a formation starts; from there
    // it tracks frame to frame as the search hint (see projectTrail).
    this._lastTrailBack = this._followGap;
    this.state = 'follow';
  }

  /**
   * Enter path mode at a given point on the road graph.
   * @param {{edge: number, dir: number, s: number}} start
   */
  enterPath(start) {
    if (!this.roads?.ready || !start) return false;
    this.leader = null;
    this._route = [{ edge: start.edge, dir: start.dir }];
    this._routeS = start.s;
    this.state = 'path';
    return true;
  }

  /**
   * Promote this vehicle to head its own chain (leader was lost). A column that
   * was driving the road network rejoins it rather than dropping to random
   * cross-country waypoints — losing the lead vehicle shouldn't take the rest
   * of the convoy off the road.
   *
   * Gated on actually being near a road. Every ground vehicle carries the
   * network, including the ones scenarios place by hand far away from it, and
   * without the range test a cross-country column that lost its leader would
   * enter path mode against the nearest edge and set off to drive there —
   * kilometres, in most missions.
   */
  promoteToLeader() {
    this.leader = null;
    this.waypoint = null;

    if (this.roads?.ready) {
      const near = this.roads.nearest(this._trailTipX, this._trailTipZ);
      // Direction is unknown here, so join facing whichever way the vehicle is
      // already travelling — resolved from the trail by _alignRouteToHeading.
      if (near && near.dist <= ROADS.rejoinMaxDistance
        && this.enterPath({ edge: near.edge, dir: 1, s: near.s })) {
        this._alignRouteToHeading();
        return;
      }
    }
    this.state = 'explore';
  }

  /** Called externally when health hits 0 — engine dies, AI stops issuing inputs. */
  disable() {
    this.state = 'disabled';
  }

  update(dt, vehicle, _terrainCollider) {
    if (this.state === 'disabled') return;

    this._recordTrail(vehicle);

    // If our leader is gone or destroyed, promote so we don't tail a wreck.
    if (this.leader && (this.leader.dead || this.leader.destroyed)) {
      this.promoteToLeader();
    }

    if (this._waypointReactionTimer > 0) this._waypointReactionTimer -= dt;

    switch (this.state) {
      case 'follow': this._tickFollow(dt, vehicle); break;
      case 'path':   this._tickPath(dt, vehicle); break;
      case 'loiter': this._tickLoiter(vehicle); break;
      case 'explore':
      default:       this._tickExplore(vehicle); break;
    }
  }

  // ── States ───────────────────────────────────────────────────────────────
  _tickExplore(vehicle) {
    const p = this.params;

    // Same reaction-delay pattern as FixedWingAIPilot: arriving clears the
    // waypoint and starts a hesitation timer; the next waypoint is picked
    // once the timer expires.
    if (this.waypoint && this._distToWaypointXZ(vehicle) < ENEMIES.waypointReachRadius) {
      this.waypoint = null;
      this._waypointReactionTimer = this._randomReactionDelay();
    }
    if (!this.waypoint && this._waypointReactionTimer <= 0) {
      this.waypoint = this._randomWaypoint();
    }

    if (!this.waypoint) {
      vehicle.desiredSpeed = p.cruiseSpeed;
      return;
    }

    const dx = this.waypoint.x - vehicle.position.x;
    const dz = this.waypoint.z - vehicle.position.z;
    vehicle.desiredHeading = Math.atan2(-dx, -dz);
    // Slowing for the turn onto a new waypoint keeps the column together as
    // well as looking right: followers are speed-capped by the same rule, so a
    // leader that swings through 180° at full cruise strings them out over
    // ground they then have limited authority to make back.
    vehicle.desiredSpeed = p.cruiseSpeed * this._cornerSpeedFactor(vehicle);
  }

  /**
   * Road following: advance along the committed route, then steer at a point
   * `lookahead` metres further on. The lookahead walks across junctions using
   * the queued traversals, so the vehicle starts its turn before reaching the
   * intersection instead of driving into it and pivoting.
   */
  _tickPath(dt, vehicle) {
    const p = this.params;
    if (!this.roads?.ready || !this._route.length) {
      // Misconfigured or the network failed to load — hold a straight cruise
      // rather than stalling, so the vehicle is still a live target.
      vehicle.desiredSpeed = p.cruiseSpeed;
      return;
    }

    // 1. Integrate progress from actual speed, then resolve any node crossings
    //    the step carried us through.
    this._routeS += vehicle.forwardSpeed * this._route[0].dir * dt;
    this._advanceRoute();

    // 2. Commit enough route ahead to cover the steering lookahead.
    const lookahead = clamp(
      Math.abs(vehicle.forwardSpeed) * ROADS.lookaheadSeconds,
      ROADS.lookaheadMin,
      ROADS.lookaheadMax,
    );
    this._ensureRoute(lookahead);

    // 3. Drift correction — see ROADS.reprojectWindow.
    this._routeS = this.roads.projectLocal(
      this._route[0].edge, this._routeS,
      vehicle.position.x, vehicle.position.z,
      ROADS.reprojectWindow,
    );

    // 4. Steer at the lookahead point.
    this._routePoint(lookahead, _sample);
    const dx = _sample.x - vehicle.position.x;
    const dz = _sample.z - vehicle.position.z;
    if (dx * dx + dz * dz > 1) vehicle.desiredHeading = Math.atan2(-dx, -dz);

    // 5. Speed — cut in proportion to how hard the turn is, plus an approach
    //    ramp for a dead-end turnaround (which is far sharper than any
    //    junction and would otherwise be entered at full cruise).
    let factor = this._cornerSpeedFactor(vehicle);

    if (this._nextIsReversal()) {
      const head = this._route[0];
      const edge = this.roads.edges[head.edge];
      const toNode = head.dir > 0 ? edge.length - this._routeS : this._routeS;
      const ramp = clamp(toNode / ROADS.turnaroundSlowRange, 0, 1);
      factor = Math.min(factor, ROADS.cornerMinSpeedFactor + (1 - ROADS.cornerMinSpeedFactor) * ramp);
    }

    vehicle.desiredSpeed = p.cruiseSpeed * ROADS.cruiseFactor * factor;
  }

  /**
   * Chase the leader's breadcrumb trail at `_followGap` metres back, rather
   * than a slot in the leader's current frame — the whole point is that this
   * still reads correctly while the leader is mid-turn, so a column rounds a
   * junction in file instead of swinging onto the new heading as a block.
   *
   * Station-keeping is measured *along the trail*, not as a straight-line
   * distance: through a corner the two disagree badly, and driving off the
   * straight-line error is what turns a lagging follower into a corner-cutting
   * one. Everything below is therefore keyed on where the vehicle actually sits
   * on the recorded line.
   */
  _tickFollow(dt, vehicle) {
    const p = this.params;
    const f = ENEMIES.vehicleFormation;
    const leadPilot = this.leader.pilot;
    const lf = this.leader.flight;

    // Along-trail position is integrated first and only then corrected against
    // the geometry, exactly as route progress is. It closes at the difference
    // between the two vehicles' speeds — the leader's motion pays out trail
    // behind it, ours reels it in. Correcting rather than measuring outright is
    // what survives a dead-end U-turn: there the trail folds back inside a
    // vehicle length, so the follower sits physically alongside a point near
    // the *tip*, measures itself as having arrived, and brakes to a standstill
    // with the entire turnaround still ahead of it.
    this._lastTrailBack = clamp(
      this._lastTrailBack + (lf.forwardSpeed - vehicle.forwardSpeed) * dt,
      0, f.trailLength,
    );
    const here = leadPilot?.projectTrail(
      vehicle.position.x, vehicle.position.z, _trailProj,
      this._lastTrailBack, f.followProjectWindow,
    );
    const distBack = here ? here.distBack : this._followGap;
    this._lastTrailBack = distBack;

    // Steer at a point a fixed lookahead further up the trail than we are —
    // clamped so it never passes the nominal slot. On station that *is* the
    // slot, so spacing behaviour is unchanged; adrift, it keeps the vehicle
    // tracking the recorded line instead of beelining at a slot far ahead.
    const aimBack = Math.max(this._followGap, distBack - f.followLookahead);
    const hit = leadPilot?.sampleTrail(aimBack, _trailHit);

    let tx, tz;
    if (hit) {
      // Lateral stagger is applied across the recorded line, so it stays a
      // side offset through corners instead of rotating with the leader.
      // Right of a forward (fx, fz) is (-fz, fx) under this sim's convention.
      tx = hit.x + -hit.tz * this._followLateral;
      tz = hit.z +  hit.tx * this._followLateral;
    } else {
      // No trail yet (leader has not moved since spawn) — close on the leader
      // itself; the gap re-establishes as soon as it starts laying crumbs.
      tx = lf.position.x;
      tz = lf.position.z;
    }

    const dx = tx - vehicle.position.x;
    const dz = tz - vehicle.position.z;
    const distXZ = Math.sqrt(dx * dx + dz * dz);
    if (distXZ > 3) vehicle.desiredHeading = Math.atan2(-dx, -dz);
    else            vehicle.desiredHeading = lf.heading;

    // Speed: whatever the vehicle ahead was doing *at this point on the line*
    // (see _recordTrail), plus a P-term on the along-trail gap error. Reading
    // the reference at the vehicle's own position rather than at the slot is
    // what keeps this stable — sampling it at the slot hands a follower still
    // in a corner the leader's corner-*exit* speed, and the column oscillates
    // into stop-and-go. Falling back to the leader's live speed only matters
    // before it has laid any trail at all.
    const speedDelta = clamp((distBack - this._followGap) * f.distanceP, -f.maxSpeedDelta, f.maxSpeedDelta);
    const reference = here ? here.speed : lf.forwardSpeed;

    // The same turn-sharpness cap the leader drives under. Without it the
    // catch-up term is free to override the corner entirely: a follower that
    // has lost a few metres entering a bend accelerates *into* it, runs wide,
    // and loses more ground for doing so — which raises the catch-up term
    // again. Capping by how hard it is actually turning breaks that loop; the
    // ground it gives up in the corner comes back on the next straight.
    vehicle.desiredSpeed = clamp(reference + speedDelta, p.minSpeed, p.maxSpeed)
      * this._cornerSpeedFactor(vehicle);
  }

  _tickLoiter(vehicle) {
    vehicle.desiredSpeed = 0;
    // Heading unchanged.
  }

  /**
   * Speed multiplier for how hard the vehicle is currently being asked to turn.
   * Shared by road following and formation keeping, because it's a property of
   * the vehicle (turn radius is v/ω, so speed is what makes a corner possible),
   * not of what it happens to be steering toward.
   */
  _cornerSpeedFactor(vehicle) {
    const err = Math.abs(wrapPi(vehicle.desiredHeading - vehicle.heading));
    return clamp(
      1 - (err / ROADS.cornerSlowAngle) * (1 - ROADS.cornerMinSpeedFactor),
      ROADS.cornerMinSpeedFactor,
      1,
    );
  }

  // ── Route helpers ────────────────────────────────────────────────────────

  /** Shift the queue for every node the current progress has run past. */
  _advanceRoute() {
    for (let guard = 0; guard < ROUTE_QUEUE_MAX; guard++) {
      const head = this._route[0];
      const edge = this.roads.edges[head.edge];

      let overshoot;
      if (head.dir > 0 && this._routeS >= edge.length) overshoot = this._routeS - edge.length;
      else if (head.dir < 0 && this._routeS <= 0)      overshoot = -this._routeS;
      else return;

      // Nothing queued behind the head yet — commit one more before shifting.
      if (this._route.length < 2) {
        this._ensureRoute(0);
        if (this._route.length < 2) {
          // Genuinely nowhere to go (isolated edge with no exits): sit at the
          // end rather than running off into open terrain.
          this._routeS = head.dir > 0 ? edge.length : 0;
          return;
        }
      }

      this._route.shift();
      const next = this._route[0];
      const nextEdge = this.roads.edges[next.edge];
      this._routeS = next.dir > 0 ? overshoot : nextEdge.length - overshoot;
    }
  }

  /** Extend the committed route until it covers `distance` metres ahead. */
  _ensureRoute(distance) {
    let available = this._distanceToRouteEnd();

    while (available < distance && this._route.length < ROUTE_QUEUE_MAX) {
      const last = this._route[this._route.length - 1];
      const node = this.roads.endNode(last.edge, last.dir);
      const exit = this.roads.pickExit(node, last.edge, last.dir);
      if (!exit) break;

      this._route.push(exit);
      available += this.roads.edges[exit.edge].length;
    }
  }

  /** Metres of committed route remaining ahead of the current position. */
  _distanceToRouteEnd() {
    const head = this._route[0];
    const headEdge = this.roads.edges[head.edge];
    let total = head.dir > 0 ? headEdge.length - this._routeS : this._routeS;
    for (let i = 1; i < this._route.length; i++) {
      total += this.roads.edges[this._route[i].edge].length;
    }
    return total;
  }

  /**
   * Point `distAhead` metres along the committed route, walking across queued
   * edges. Tangent is in the direction of travel.
   */
  _routePoint(distAhead, out) {
    return this.roads.sampleRoute(this._route, this._routeS, distAhead, out);
  }

  /** True when the next committed traversal doubles back — i.e. a dead end. */
  _nextIsReversal() {
    if (this._route.length < 2) return false;
    return this._route[1].edge === this._route[0].edge
        && this._route[1].dir === -this._route[0].dir;
  }

  /**
   * Flip the head traversal if it points against the vehicle's travel. Used
   * when joining the network from an unknown heading (leader promotion), where
   * the nearest-point query gives a position but no sense of direction.
   */
  _alignRouteToHeading() {
    const head = this._route[0];
    this.roads.sample(head.edge, this._routeS, _sample);
    // Trail tip minus the previous crumb is the direction we're actually going.
    if (this._trailCount < 1) return;
    const px = this._trailAt(0, 0), pz = this._trailAt(0, 1);
    const vx = this._trailTipX - px, vz = this._trailTipZ - pz;
    if (vx * vx + vz * vz < 1e-6) return;
    if (vx * _sample.tx + vz * _sample.tz < 0) head.dir = -1;
  }

  // ── Breadcrumbs ──────────────────────────────────────────────────────────

  /**
   * Append the vehicle's position and speed to the trail once it has moved a
   * full sample interval. Recorded for every driver, not just leaders:
   * formations are chains, so all but the last vehicle are somebody's leader.
   *
   * Speed is recorded, not just position, because a follower has to slow where
   * the vehicle ahead slowed — not where it happens to be slow *now*. Taking
   * the leader's live speed means that by the time a follower reaches a corner
   * the leader is already past it and back at cruise, so the follower takes the
   * turn at full speed, swings wide off the road, and lays that corner-cut as
   * the trail for the vehicle behind it. The error compounds down the column.
   */
  _recordTrail(vehicle) {
    const x = vehicle.position.x;
    const z = vehicle.position.z;
    this._trailTipX = x;
    this._trailTipZ = z;
    this._trailTipSpeed = vehicle.forwardSpeed;

    if (this._trailCount > 0) {
      const lastX = this._trailAt(0, 0);
      const lastZ = this._trailAt(0, 1);
      const dx = x - lastX, dz = z - lastZ;
      const step = ENEMIES.vehicleFormation.trailSampleDist;
      if (dx * dx + dz * dz < step * step) return;
    }

    this._trailHead = this._trailCount === 0 ? 0 : (this._trailHead + 1) % this._trailCap;
    const o = this._trailHead * TRAIL_STRIDE;
    this._trail[o]     = x;
    this._trail[o + 1] = z;
    this._trail[o + 2] = vehicle.forwardSpeed;
    if (this._trailCount < this._trailCap) this._trailCount++;
  }

  /**
   * Seed the trail with a line the vehicle is treated as having just driven,
   * oldest point first. Spawn-time only: without it a freshly placed column has
   * no history, every follower aims straight at its leader, and the formation
   * visibly collapses into a huddle before spreading back out on the first few
   * seconds of driving.
   *
   * Primed at rest: vehicles spawn stationary, so a trail claiming cruise speed
   * would have the whole column lunge forward before the leader has moved. At
   * zero the column pulls away in sequence as the leader's real crumbs reach
   * each vehicle, which is both correct and what a convoy looks like.
   *
   * @param {ArrayLike<number>} pts — flat [x, z, x, z, …], oldest → newest
   */
  primeTrail(pts) {
    this._trailCount = 0;
    this._trailHead = 0;
    for (let i = 0; i + 1 < pts.length; i += 2) {
      this._trailHead = this._trailCount === 0 ? 0 : (this._trailHead + 1) % this._trailCap;
      const o = this._trailHead * TRAIL_STRIDE;
      this._trail[o]     = pts[i];
      this._trail[o + 1] = pts[i + 1];
      this._trail[o + 2] = 0;
      if (this._trailCount < this._trailCap) this._trailCount++;
    }
    if (this._trailCount > 0) {
      this._trailTipX = this._trailAt(0, 0);
      this._trailTipZ = this._trailAt(0, 1);
      this._trailTipSpeed = 0;
    }
  }

  /**
   * Point `distBack` metres behind this vehicle along the line it drove.
   *
   * Walks back from the live position (not the newest crumb, which lags it by
   * up to one sample interval) so the gap a follower holds doesn't jitter at
   * the recording cadence. Returns null if nothing has been recorded; if the
   * trail is shorter than `distBack` it returns the oldest point with
   * `short = true`, which reads as "close up" to the caller.
   *
   * @param {object} out — mutated with { x, z, tx, tz, speed, short }
   */
  sampleTrail(distBack, out) {
    if (this._trailCount === 0) return null;

    let remaining = distBack;
    let ax = this._trailTipX, az = this._trailTipZ, aSpeed = this._trailTipSpeed;

    for (let k = 0; k < this._trailCount; k++) {
      const bx = this._trailAt(k, 0);
      const bz = this._trailAt(k, 1);
      const bSpeed = this._trailAt(k, 2);
      const seg = Math.hypot(bx - ax, bz - az);

      if (seg >= remaining) {
        const t = seg > 1e-6 ? remaining / seg : 0;
        out.x = ax + (bx - ax) * t;
        out.z = az + (bz - az) * t;
        out.speed = aSpeed + (bSpeed - aSpeed) * t;
        // Tangent points the way the vehicle was travelling: newer → older
        // reversed, i.e. from b toward a.
        const inv = seg > 1e-6 ? 1 / seg : 0;
        out.tx = (ax - bx) * inv;
        out.tz = (az - bz) * inv;
        out.short = false;
        return out;
      }

      remaining -= seg;
      ax = bx;
      az = bz;
      aSpeed = bSpeed;
    }

    // Ran out of history — hand back the oldest crumb.
    out.x = ax;
    out.z = az;
    out.speed = aSpeed;
    const tipDX = this._trailTipX - ax, tipDZ = this._trailTipZ - az;
    const len = Math.hypot(tipDX, tipDZ);
    out.tx = len > 1e-6 ? tipDX / len : 0;
    out.tz = len > 1e-6 ? tipDZ / len : 0;
    out.short = true;
    return out;
  }

  /**
   * Locate a world XZ on this vehicle's trail: how far back along the recorded
   * line the closest point sits, and the speed that was being held there.
   *
   * This is the along-trail counterpart to the route re-projection in path
   * mode, and is local for the same reason. A vehicle rounding a tight corner
   * lays a trail that folds back within a few metres of itself — at the BTR's
   * ~6 m turning radius when slowed for a junction, easily so — and a global
   * nearest-point search flips between the two branches from frame to frame.
   * `distBack` then jumps by tens of metres, which slams the speed controller
   * against both its limits and throws the aim point across the corner. The
   * caller passes last frame's answer as `hintBack`, and at ~0.2 m of travel
   * per frame any modest window is ample.
   *
   * @param {object} out — mutated with { distBack, speed, dist }
   * @param {number} hintBack — previous result; negative to search the whole trail
   */
  projectTrail(x, z, out, hintBack = -1, window = 0) {
    if (this._trailCount === 0) return null;

    const bounded = hintBack >= 0;
    const lo = bounded ? hintBack - window : -Infinity;
    const hi = bounded ? hintBack + window : Infinity;

    let acc = 0;                                     // tip → `a` along the trail
    let ax = this._trailTipX, az = this._trailTipZ, aSpeed = this._trailTipSpeed;
    let bestD2 = Infinity, bestBack = bounded ? hintBack : 0, bestSpeed = aSpeed;

    for (let k = 0; k < this._trailCount; k++) {
      const bx = this._trailAt(k, 0);
      const bz = this._trailAt(k, 1);
      const bSpeed = this._trailAt(k, 2);

      const dx = bx - ax, dz = bz - az;
      const seg2 = dx * dx + dz * dz;
      if (seg2 > 1e-9) {
        const segLen = Math.sqrt(seg2);
        if (acc > hi) break;                         // past the window — done
        if (acc + segLen >= lo) {
          const t = clamp(((x - ax) * dx + (z - az) * dz) / seg2, 0, 1);
          const px = ax + dx * t, pz = az + dz * t;
          const d2 = (x - px) * (x - px) + (z - pz) * (z - pz);
          if (d2 < bestD2) {
            bestD2 = d2;
            bestBack = acc + segLen * t;
            bestSpeed = aSpeed + (bSpeed - aSpeed) * t;
          }
        }
        acc += segLen;
      }

      ax = bx; az = bz; aSpeed = bSpeed;
    }

    out.distBack = bounded ? clamp(bestBack, Math.max(0, lo), hi) : bestBack;
    out.speed = bestSpeed;
    out.dist = bestD2 === Infinity ? 0 : Math.sqrt(bestD2);
    return out;
  }

  /** k-th most recent trail sample (0 = newest); `i` is 0=x, 1=z, 2=speed. */
  _trailAt(k, i) {
    const slot = (this._trailHead - k + this._trailCap * 2) % this._trailCap;
    return this._trail[slot * TRAIL_STRIDE + i];
  }

  // ── Helpers ──────────────────────────────────────────────────────────────
  _distToWaypointXZ(vehicle) {
    if (!this.waypoint) return Infinity;
    const dx = this.waypoint.x - vehicle.position.x;
    const dz = this.waypoint.z - vehicle.position.z;
    return Math.sqrt(dx * dx + dz * dz);
  }

  _randomWaypoint() {
    const [minX, minZ] = ENEMIES.worldMin;
    const [maxX, maxZ] = ENEMIES.worldMax;
    return {
      x: rand(minX, maxX),
      z: rand(minZ, maxZ),
    };
  }
}
